import React, {useEffect, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import FfairModal from "view/components/modal/FfairModal";
import FlexBox from "view/components/Layout/FlexBox";
import Form, {BasicSelect, BasicInput} from "view/components/Form";
import Button from "@material-ui/core/Button";
import {Typography, TextField} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {apiV2, selectUser} from "api/firebase";
import BackdropLoader from "view/components/BackdropLoader";
import {useCurrentShow} from "hooks";
import {useOrganisers} from "store/organisers/organiser.store";
import {
  OrganiserToShowRole,
  PermissionPackage,
  IShow,
} from "@evennt-tech/api-v2-core";
import Alert from "view/components/Alert";
import {SHOWS_LIST_PATH} from "view/routes";
import {useNavigate} from "react-router-dom";
import {useAddressLocations} from "hooks/useAddressLocations";
import useStyles from "./styles";

interface ForceSetCountryModalProps {
  showId: string;
}

function ForceSetCountryModal({showId}: ForceSetCountryModalProps) {
  const classes = useStyles();
  const history = useNavigate();
  const userId = selectUser()?.id;

  const [show] = useCurrentShow({showId});
  const {
    selected: organiser,
    fetchOneByUserId,
    getOrganiserShowRole,
  } = useOrganisers();

  const [isSomeAddressDataMissing, setIsSomeAddressDataMissing] = useState<
    boolean
  >(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (userId) fetchOneByUserId({userId});
  }, [userId, fetchOneByUserId]);

  useEffect(() => {
    if (!show || !organiser) return;
    if (
      show.venueCountryId &&
      show.venueAddress1 &&
      show.venueCity &&
      show.postcode
    )
      return;

    getOrganiserShowRole({showId, organiserId: organiser.id}).then(
      (rolesByShowId) => {
        if (rolesByShowId !== OrganiserToShowRole.SHOW_ADMIN) return;

        const isTaskdIncluded =
          show.permissionPackages?.includes(PermissionPackage.TASKD) &&
          organiser?.permissionPackages?.includes(PermissionPackage.TASKD);

        if (!isTaskdIncluded) return;

        setIsSomeAddressDataMissing(
          !show.venueCountryId ||
            !show.venueAddress1 ||
            !show.venueCity ||
            !show.postcode
        );
      }
    );
  }, [organiser, show, showId, getOrganiserShowRole]);

  const form = useForm<
    Pick<
      IShow,
      | "venueState"
      | "venueCity"
      | "postcode"
      | "venueAddress1"
      | "venueAddress2"
      | "venueCountryId"
    >
  >({
    defaultValues: {
      venueCountryId: "",
      venueState: "",
      postcode: "",
      venueCity: "",
      venueAddress1: "",
      venueAddress2: "",
    },
    mode: "onChange",
  });

  const {
    watch,
    setValue,
    control,
    formState: {isValid},
  } = form;

  const venueCountryId = watch("venueCountryId") || "";
  const venueState = watch("venueState") || "";

  const {countries, states, cities, isLoadingLocations} = useAddressLocations({
    venueCountryId,
    venueState,
    setValue,
  });

  const onConfirm = form.handleSubmit(async (data) => {
    setIsLoading(true);
    try {
      await apiV2.show.update({id: showId, ...data});
    } finally {
      setIsLoading(false);
      setIsSomeAddressDataMissing(false);
    }
  });

  const goToAllShows = () => history(SHOWS_LIST_PATH);

  return (
    <FfairModal open={isSomeAddressDataMissing} disableClose>
      <Form className={classes.root} form={form} onSubmit={onConfirm}>
        <BackdropLoader isLoading={isLoading || isLoadingLocations} />
        <Typography className={classes.header}>
          Venue Details for the Show
        </Typography>

        <FlexBox column>
          <BasicSelect
            label="Country"
            name="venueCountryId"
            required
            options={countries.map((country) => ({
              value: country.id,
              label: country.name,
            }))}
          />

          {states.length > 0 && (
            <BasicSelect
              label="State / Region"
              name="venueState"
              required
              options={states.map((state) => ({
                value: state.name,
                label: state.name,
              }))}
            />
          )}

          <div style={{marginBottom: "24px"}}>
            <Controller
              name="venueCity"
              control={control}
              rules={{required: "City is required"}}
              render={({field, fieldState: {error}}) => (
                <Autocomplete
                  {...field}
                  freeSolo
                  options={cities.map((city) => city.name)}
                  disabled={states.length > 0 && !venueState}
                  onChange={(_, data) => field.onChange(data)}
                  onInputChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City *"
                      error={!!error}
                      helperText={error?.message}
                      variant="outlined"
                    />
                  )}
                />
              )}
            />
          </div>

          <BasicInput label="Address Line 1" name="venueAddress1" required />
          {/* <BasicInput */}
          {/*   label="Address Line 2 (Optional)" */}
          {/*   name="venueAddress2" */}
          {/*   required={false} */}
          {/* /> */}
          <BasicInput label="Postcode" name="postcode" required />
        </FlexBox>

        <Alert className={classes.alert} severity="info">
          These settings are required to continue
        </Alert>

        <FlexBox column gap={3} mt={2}>
          <FlexBox justifyContent="space-between" gap={1}>
            <Button color="primary" onClick={goToAllShows}>
              back to all shows
            </Button>
            <Button
              disabled={!isValid}
              color="primary"
              variant="text"
              type="submit"
            >
              Save
            </Button>
          </FlexBox>
        </FlexBox>
      </Form>
    </FfairModal>
  );
}

export default ForceSetCountryModal;
